import { all, takeLatest, put, fork, call } from 'redux-saga/effects';
// import { stopSubmit } from 'redux-form';
import { toast } from 'sonner';
import { t } from 'copy/t';
import { capture } from 'utils/posthog';
import { getSessionFlow } from 'containers/App/sagas';
import { navigate } from 'navigation';
import {
  FETCH_CHANNELS_REQUEST,
  FETCH_CHANNEL_DETAIL_REQUEST,
  FETCH_UPDATED_CHANNEL_DETAIL_REQUEST,
  FETCH_SLUG_VALIDATE_REQUEST,
  FETCH_CLIENTS_REQUEST,
  FETCH_THEMES_REQUEST,
  FETCH_MODES_REQUEST,
  POST_CHANNEL_REQUEST,
  PATCH_CHANNEL_SUCCESS,
  PUT_CHANNEL_FAILURE,
  PUT_CHANNEL_SUCCESS,
  PATCH_CHANNEL_REQUEST,
  DELETE_CHANNEL_REQUEST,
  DELETE_CHANNEL_SUCCESS,
  DELETE_CHANNEL_FAILURE,
  ARCHIVE_CHANNEL_REQUEST,
  ARCHIVE_CHANNEL_SUCCESS,
  ARCHIVE_CHANNEL_FAILURE,
} from './constants';
import {
  fetchChannelsSuccess,
  fetchChannelsFailure,
  patchChannelSuccess,
  putChannelSuccess,
  putChannelFailure,
  fetchClientsSuccess,
  fetchClientsFailure,
  fetchThemesSuccess,
  fetchThemesFailure,
  fetchModesSuccess,
  fetchModesFailure,
  validateChannelSlugFailure,
  validateChannelSlugSuccess,
  deleteChannelSuccess,
  deleteChannelFailure,
  archiveChannelSuccess,
  archiveChannelFailure,
  fetchChannelDetailSuccess,
  fetchChannelDetailFailure
} from './actions';
import { authedApiSaga } from 'sagas';
import {
  getChannels,
  postChannel,
  getClients,
  patchChannel,
  formPatchChannel,
  getChannelThemes,
  getModes,
  checkSlugAvailability,
  deleteChannel,
  archiveChannel,
  getChannelDetail,
} from 'utils/api';
import { isString, isObject } from 'utils/helpers';

const getFetchParams = (params) => {
  return params
    ? `?${Object.keys(params).map(k => params[k] ? `${k}=${params[k]}` : null).filter(x => x).join('&')}`
    : '';
}

export const getUpdatedBrandParams = ({
  big_logo_image_url,
  overlay_image,
  gallery_banner,
  photo_filters,
}) => {
  const app_logo_image = isObject(big_logo_image_url)
    ? big_logo_image_url[0]
    : big_logo_image_url === ""
    ? null
    : undefined;
  const app_background_image = isObject(overlay_image)
    ? overlay_image[0]
    : overlay_image === ""
    ? null
    : undefined;
  const gallery_logo = isObject(gallery_banner)
    ? gallery_banner[0]
    : gallery_banner === ""
    ? null
    : undefined;

  // Send photo_filters only when the user has made changes (new uploads
  // or explicit removal). After componentDidMount normalisation, each entry
  // is { file: File | {name: url}, selected }. Map to:
  //   - File (new upload) → sent as multipart file
  //   - {name: url} (existing filter to retain) → sent as reference
  // The backend partitions by respond_to?(:original_filename) and matches
  // existing filters by filename extracted from the name/URL.
  let photo_filters_img = undefined;
  if (isObject(photo_filters) && Array.isArray(photo_filters)) {
    if (photo_filters.length === 0) {
      photo_filters_img = null;
    } else if (photo_filters.some((x) => x && x.file)) {
      // Items have gone through componentDidMount normalisation:
      // each entry is { file: File | {name: url}, selected }.
      // Map to File (new upload) or {name: url} (existing filter to retain).
      photo_filters_img = photo_filters.filter((x) => x).map((x) =>
        x.file ? x.file : { name: x.image || x.name }
      );
    }
    // else: raw API-shape filters with no .file property → undefined (no change)
  } else if (isObject(photo_filters)) {
    // A multi-file browse selection can reach here as a FileList (an object, not
    // an Array). Spread every file so all selected frames are serialized — taking
    // [0] here dropped every file after the first.
    photo_filters_img = Array.from(photo_filters);
  }

  return {
    big_logo_image_url: app_logo_image,
    overlay_image: app_background_image,
    gallery_banner: gallery_logo,
    photo_filters: photo_filters_img,
  };
};

export function* fetchChannelsFlow({ params }) {
  yield* authedApiSaga(token => getChannels(token, getFetchParams(params)), [], fetchChannelsSuccess, fetchChannelsFailure);
}

// Both detail flows echo the request's id back on its response so the reducer
// can drop a response that a newer request has already superseded.
export function* fetchChannelDetailFlow({ id, requestId }) {
  yield* authedApiSaga(
    token => getChannelDetail(token, id),
    [],
    (payload) => fetchChannelDetailSuccess(payload, requestId),
    (error) => fetchChannelDetailFailure(error, requestId)
  );
}

export function* fetchUpdatedChannelDetailFlow({ id, requestId }) {
  yield* authedApiSaga(
    token => getChannelDetail(token, id),
    [],
    (payload) => fetchChannelDetailSuccess(payload, requestId),
    (error) => fetchChannelDetailFailure(error, requestId)
  );
}

export function* validateChannelSlugRequestFlow({ slug, channelId }) {
  yield* authedApiSaga(
    (token) => checkSlugAvailability(token, slug, channelId),
    [],
    validateChannelSlugSuccess,
    validateChannelSlugFailure
  );
}
export function* validateChannelSlugSuccessFlow({ payload }) {
  yield put(validateChannelSlugSuccess(payload));
}

export function* validateChannelSlugFailureFlow({ payload }) {
  yield put(validateChannelSlugFailure(payload));
}

export function* putChannelSuccessFlow({ payload } = {}) {
  // Leave the wizard only once the server has the channel. Navigating first
  // dropped the typed form on any 403, 422 or timeout.
  yield call(navigate, '/channels');
  toast.success(t('toast.channel.created'));
  // Measurement: id only (from the normalized allChannels map when present) —
  // never the channel's name, client, or brand payload.
  const [channelId] = Object.keys(payload?.allChannels || {});
  capture('dashboard_channel_saved', { action: 'create', channel_id: channelId });
}

export function* postChannelFlow({ payload }) {
  const clientData = payload.client;
  const client_id =
    isString(clientData) ? undefined : clientData ? clientData : undefined;

  const args = [{
    client_name: isString(clientData) ? clientData : undefined,
    client_id,
    ...payload,
    ...getUpdatedBrandParams(payload),
  }];
  yield* authedApiSaga(postChannel, args, putChannelSuccess, putChannelFailure);
  yield fork(getSessionFlow);
}

export function* patchChannelFlow({ payload: { id, data } }) {
  const {
    client: clientData,
    hashtag_image,
    hashtag_image_url,
    ...rest
  } = data;

  const brandParams = getUpdatedBrandParams(rest);

  yield* authedApiSaga(
    patchChannel,
    [
      id,
      {
        ...rest,
        client_name: isString(clientData) ? clientData : undefined,
        client_id: isString(clientData) ? undefined : clientData,
        ...brandParams,
      },
    ],
    patchChannelSuccess,
    putChannelFailure
  );
}

export function* channelSuccessFlowForPatch({ payload: { allChannels }}) {
  // Pattern match on the first and only id returned
  const [id]       = Object.keys(allChannels),
        nextRoute  = `/channels/${id}/edit`;
  toast.success(t('toast.channel.saved'));
  // Measurement: id only — see putChannelSuccessFlow.
  capture('dashboard_channel_saved', { action: 'edit', channel_id: id });
  yield all([
    call(navigate, nextRoute),
  ]);
}

export function* channelFailureFlow({ payload: { errors }}) {
  const message = errors?.error || errors?.message || t('toast.channel.saveFailed');
  toast.error(message);
}

export function* deleteChannelSuccessFlow() {
  // The reducer already removed the row in place (DELETE_CHANNEL_SUCCESS carries
  // the channelId). The owner stays on the channels list — no redirect.
  toast.success(t('toast.channel.deleted'));
}

export function* deleteChannelFailureFlow() {
  toast.error(t('toast.channel.deleteFailed'));
}

export function* deleteChannelFlow( {channelId} ) {
  // Bind the channelId into the success action (not the API body) so the
  // reducer's in-place row removal targets the right channel.
  yield* authedApiSaga(
    deleteChannel,
    [{ channelId }],
    () => deleteChannelSuccess(channelId),
    deleteChannelFailure
  );
}

export function* archiveChannelSuccessFlow() {
  toast.success(t('toast.channel.archived'));
}

export function* archiveChannelFailureFlow() {
  toast.error(t('toast.channel.archiveFailed'));
}

export function* archiveChannelFlow( {channelId} ) {
  yield* authedApiSaga(
    archiveChannel,
    [{ channelId }],
    () => archiveChannelSuccess(channelId),
    archiveChannelFailure
  );
}

export function* fetchClientsFlow({ payload: { q } }) {
  yield* authedApiSaga(getClients, [
    { q }
  ], fetchClientsSuccess, fetchClientsFailure);
}

export function* fetchThemesFlow() {
  yield* authedApiSaga(getChannelThemes, [
  ], fetchThemesSuccess, fetchThemesFailure);
}

export function* fetchModesFlow() {
  yield* authedApiSaga(getModes, [
  ], fetchModesSuccess, fetchModesFailure);
}

export default function* watchChannels() {
  yield all([
    takeLatest(FETCH_SLUG_VALIDATE_REQUEST, validateChannelSlugRequestFlow),
    takeLatest(FETCH_CHANNELS_REQUEST, fetchChannelsFlow),
    takeLatest(FETCH_UPDATED_CHANNEL_DETAIL_REQUEST, fetchUpdatedChannelDetailFlow),
    takeLatest(FETCH_CHANNEL_DETAIL_REQUEST, fetchChannelDetailFlow),
    takeLatest(FETCH_CLIENTS_REQUEST, fetchClientsFlow),
    takeLatest(FETCH_THEMES_REQUEST, fetchThemesFlow),
    takeLatest(FETCH_MODES_REQUEST, fetchModesFlow),
    takeLatest(POST_CHANNEL_REQUEST, postChannelFlow),
    takeLatest(PUT_CHANNEL_SUCCESS, putChannelSuccessFlow),
    takeLatest(PATCH_CHANNEL_REQUEST, patchChannelFlow),
    takeLatest(PUT_CHANNEL_FAILURE, channelFailureFlow),
    takeLatest(PATCH_CHANNEL_SUCCESS, channelSuccessFlowForPatch),
    takeLatest(DELETE_CHANNEL_REQUEST, deleteChannelFlow),
    takeLatest(DELETE_CHANNEL_SUCCESS, deleteChannelSuccessFlow),
    takeLatest(DELETE_CHANNEL_FAILURE, deleteChannelFailureFlow),
    takeLatest(ARCHIVE_CHANNEL_REQUEST, archiveChannelFlow),
    takeLatest(ARCHIVE_CHANNEL_SUCCESS, archiveChannelSuccessFlow),
    takeLatest(ARCHIVE_CHANNEL_FAILURE, archiveChannelFailureFlow),
  ]);
}
